import React from 'react';

import { IDENTITY_HUES } from '../../config/brand';
import { normalizeColor } from '../../utils/elementFormat/color';

/**
 * A `color` element's value as every read surface shows it: a swatch, then
 * the stored hex (docs/plans/data-types-second-pass.md §4.1). The text is the
 * same formatValue prints everywhere else, so a title, an export and a filter
 * all read "#1f6feb"; the swatch is what tells a person which blue.
 *
 * The swatch is painted with an INLINE style, which is the standards' second
 * exception to "every color is a token" (CLAUDE.md, docs/platform-standards.md
 * § 0.1): the color is the item's data, not the theme's, exactly as an
 * identity hue is. Nothing else about it is: its size, ring and radius are
 * tokens in src/css/components/data-types.css.
 *
 * A stored value the color rule cannot read (a "navy" a short text held
 * before the element became a color) gets no swatch — it is printed, never
 * painted — so the reader sees the text that is really stored.
 */

/**
 * The color picker's presets: the identity hues, the icon + color modal's
 * palette (components/common/IconColorPicker.js), in the stored spelling.
 */
export const COLOR_PRESETS = [{
	label: 'Suggested',
	colors: IDENTITY_HUES.map(hue => normalizeColor(hue.value).value),
	defaultOpen: true,
}];

/** The stored "#rrggbb" a value paints as, or null when it is not a color. */
export function swatchColorOf(value) {
	const color = normalizeColor(typeof value === 'string' ? value : '');
	return color.valid ? color.value : null;
}

/** The square alone; nothing for a value that is not a color. */
export default function ColorSwatch({ value, className = '' }) {
	const color = swatchColorOf(value);
	if (!color) return null;
	return (
		<span
			className={`color-swatch ${className}`.trim()}
			// User data, not theme: the standards' second exception.
			style={{ backgroundColor: color }}
			aria-hidden='true'
			data-color={color}
		/>
	);
}
